/** Platform AI catalog (`/catalog`). */

import { AIModel, AIService, HostingPricingRule, ServiceCategory } from '../../schemas/platform';
import { BackendClient, CatalogQuery, QueryEnvelope } from './BackendClient';

export class CatalogClient extends BackendClient {
  constructor(apiKey?: string, webBackendUrl?: string) {
    super(apiKey, webBackendUrl);
    this.addEndpoint('catalog/services', 'GET', 'optional');
    this.addEndpoint('catalog/models', 'GET', 'none');
    this.addEndpoint('catalog/categories', 'GET', 'none');
    this.addEndpoint('catalog/pricing-rules', 'GET', 'none');
  }

  async queryServices(query: CatalogQuery = {}): Promise<QueryEnvelope<AIService>> {
    const raw = await this.getJson(
      'catalog/services',
      this.catalogQuery(query),
      query.mine ? 'required' : 'optional',
    );
    const envelope = this.asEnvelope<Record<string, any>>(raw);
    return {
      ...envelope,
      entities: envelope.entities.map((row) => new AIService(row)),
    };
  }

  async getService(
    idOrName: string,
    query: Pick<CatalogQuery, 'expand' | 'fields' | 'filters'> = {},
  ): Promise<AIService | null> {
    const route = `catalog/services/${encodeURIComponent(idOrName)}`;
    this.addEndpoint(route, 'GET', 'optional');
    const raw = await this.getJson(route, this.catalogQuery(query));
    return raw ? new AIService(raw) : null;
  }

  async queryModels(query: CatalogQuery = {}): Promise<QueryEnvelope<AIModel>> {
    const raw = await this.getJson('catalog/models', this.catalogQuery(query));
    const envelope = this.asEnvelope<Record<string, any>>(raw);
    return {
      ...envelope,
      entities: envelope.entities.map((row) => new AIModel(row)),
    };
  }

  async getModel(
    idOrName: string,
    query: Pick<CatalogQuery, 'expand' | 'fields'> = {},
  ): Promise<AIModel | null> {
    const route = `catalog/models/${encodeURIComponent(idOrName)}`;
    this.addEndpoint(route, 'GET', 'none');
    const raw = await this.getJson(route, this.catalogQuery(query));
    return raw ? new AIModel(raw) : null;
  }

  async listCategories(): Promise<ServiceCategory[]> {
    const raw = await this.getJson('catalog/categories');
    const rows = Array.isArray(raw) ? raw : raw?.entities ?? [];
    return rows.map((row: any) => new ServiceCategory(row));
  }

  async listPricingRules(activeOnly = true): Promise<HostingPricingRule[]> {
    const raw = await this.getJson('catalog/pricing-rules', { active_only: activeOnly });
    const rows = Array.isArray(raw) ? raw : raw?.entities ?? [];
    return rows.map((row: any) => new HostingPricingRule(row));
  }
}
